import axios, { ApiResponse, ApiRoutes, ID_PLACEHOLDER, RequestStatus } from "@/api/client";
import { deleteResource, getAllPages, getResource, getResourcePage, handleApiError } from "@/api/helpers";
import { ExistingModel, ExistingRecipient, PaginationParams, PaginationResponse } from "@/types";

const getModels = async (
  paginationParams: PaginationParams,
  filters: Record<string, string> = {},
): Promise<PaginationResponse<ExistingModel>> => {
  return getResourcePage<ExistingModel, { models: ExistingModel[] | null }>(
    ApiRoutes.MODELS,
    paginationParams,
    (response) => response.data.data.models ?? [],
    "Failed to fetch model configs.",
    filters,
  );
};

const getAllModels = async (): Promise<ExistingModel[]> => getAllPages(getModels);

const getPaginatedModelsForRecipient = async (
  recipientId: string,
  paginationParams: PaginationParams,
): Promise<PaginationResponse<ExistingModel>> => {
  return getResourcePage<ExistingModel, { models: ExistingModel[] | null }>(
    `${ApiRoutes.RECIPIENTS}/${recipientId}${ApiRoutes.MODELS}`,
    paginationParams,
    (response) => response.data.data.models ?? [],
    "Failed to fetch model configs for recipient.",
  );
};

const getModelsForRecipient = async (recipientId: ExistingRecipient["id"]): Promise<ExistingModel[]> =>
  getAllPages((params) => getPaginatedModelsForRecipient(recipientId, params));

const getModel = async (modelID: ExistingModel["id"]): Promise<ExistingModel> =>
  getResource<ExistingModel, { model: ExistingModel }>(
    `${ApiRoutes.MODELS}/${modelID}`,
    (response) => response.data.data.model,
    "Failed to fetch model config.",
  );

const validateModel: (m: ExistingModel) => Promise<RequestStatus> = (model) => {
  const url = ApiRoutes.VALIDATE_EXPORT_MODEL.replace(ID_PLACEHOLDER, model.id);
  return axios
    .post(url)
    .then((response: ApiResponse<RequestStatus>) => response.data.data)
    .catch(handleApiError("Export model validation failed."));
};

const deleteModel = (id: string): Promise<void> =>
  deleteResource(`${ApiRoutes.MODELS}/${id}`, "Failed to delete model.");

const ModelsService = {
  getModels,
  getAllModels,
  getModelsForRecipient,
  getModel,
  validateModel,
  deleteModel,
};

export default ModelsService;
